import { Alert, Box, Snackbar, Typography } from '@mui/material';
import type { FC } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoginForm } from '~/components/organisms/LoginForm';
import { HOME_PATH } from '~/constants';
import { useSnackbar } from '~/hooks/useSnackbar';
import { Logger } from '~/lib/logger';
import { useApi } from '~/providers/ApiProvider';
import { useAuth } from '~/providers/AuthProvider';
import type { LoginFormType } from '~/types/Login';

const logger = new Logger('Login');

export const Login: FC = () => {
  const { setToken, sessionExpired, setSessionExpired } = useAuth();
  const { warehouseApi } = useApi();
  const navigate = useNavigate();
  const { showSnackbar, closeSnackbar, snackbar } = useSnackbar();

  useEffect(() => {
    if (sessionExpired) {
      showSnackbar('Session expired, please log in again', 'info', 'Session Expired');
      setTimeout(() => setSessionExpired(false), 6000);
    }
  }, [sessionExpired, setSessionExpired, showSnackbar]);

  const { mutate, isPending } = warehouseApi.login.useMutation({
    onSuccess: (data) => {
      const token = data.body.data.token;
      if (!token) {
        logger.error('Token not found in response');
        throw new Error('Token not found in response');
      }

      setToken(data.body.data.token);
      navigate(HOME_PATH);
    },
    onError: (error) => {
      const errorMsg = 'Invalid email or password';
      logger.error(errorMsg, error);
      showSnackbar(`${errorMsg}. Please try again.`, 'error', 'Invalid Credentials');
    },
  });

  const onSubmit = (data: LoginFormType) => {
    mutate({ body: { data } });
  };

  return (
    <>
      <Box>
        <Typography component="h1" variant="subtitle1" fontWeight={600} textAlign="center" mb={4.75}>
          Supplier Login
        </Typography>

        <LoginForm onSubmit={onSubmit} isPending={isPending} />
      </Box>

      <Snackbar
        open={snackbar.isOpen && !!snackbar.message}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.type} variant="standard" title={snackbar.title} onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
